import { inject, Injectable } from '@angular/core';
import { DashboardApiService } from '@core/api/dashboard-api.service';
import {
  DashboardConfig,
  DashboardConfigSchema,
} from '@root/app/services/dashboard-config/dashboard-charts-config.schema';
import { ToolControls } from '@dashboard/components/dynamic-dashboard-wrapper/tool-controls';
import { ChartIds } from '@dashboard/components/vessel-info/vessel-info-details/charts-tab/charts.model';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DashboardConfigService {
  private _customApi = inject(DashboardApiService);

  private config!: DashboardConfig;

  private Notify = inject(NzNotificationService);

  async load(): Promise<void> {
    this.Notify.remove();
    let raw: unknown;

    try {
      raw = await firstValueFrom(this._customApi.getDashboardTemplate());
    } catch (e) {
      console.error('Failed to load dashboard config file', e);
      this.Notify.error(
        'Error',
        'Dashboard config file is missing or unreachable. Please contact the administrator.'
      );
      throw new Error('Dashboard config file is missing or unreachable');
    }

    const parsed = DashboardConfigSchema.safeParse(dropUnknownKeys(raw));

    if (!parsed.success) {
      const errors = parsed.error.issues.map((issue) => ({
        path: issue.path.join('.'),
        message: issue.message,
      }));

      console.error('Dashboard config schema validation failed:', errors);
      this.Notify.error(
        'Error',
        'Dashboard config schema validation failed. Please contact the administrator.'
      );
      throw new Error('Dashboard config JSON does not match required schema');
    }

    this.config = parsed.data;
  }

  public getConfig() {
    return this.config;
  }
}

/**
 * The configuration file is shared with naviqore-tools (one mounted `configuration.json`), so it
 * can name a module or chart this build doesn't know yet — e.g. a tools-only module added before
 * the portal ships its button. An unknown key is dropped with a warning instead of failing the
 * whole schema, which would otherwise stop the dashboard from loading at all.
 */
function dropUnknownKeys(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const config = raw as { charts?: unknown; controls?: unknown };
  const knownControls = new Set<string>(ToolControls);
  const knownCharts = new Set<string>(Object.values(ChartIds) as string[]);

  const keep = (entries: unknown, known: Set<string>, kind: string): unknown => {
    if (!Array.isArray(entries)) return entries;
    return entries.filter((entry) => {
      const key = (entry as { key?: unknown } | null)?.key;
      if (typeof key === 'string' && !known.has(key)) {
        console.warn(`Dashboard config: ignoring unknown ${kind} key "${key}"`);
        return false;
      }
      return true;
    });
  };

  return {
    ...config,
    charts: keep(config.charts, knownCharts, 'chart'),
    controls: keep(config.controls, knownControls, 'control'),
  };
}
